import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
	Box,
	Button,
	CircularProgress,
	FormControl,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	Typography,
	Switch,
} from "@mui/material";
import axios from "axios";
import LineChartComponent from "../../charts/LineChart";
import {
	fetchData,
	FetchDataFailureAction,
	FetchDataSuccessAction,
} from "../../../store/actions";
import { getUtilizationData, jiraChartResponseProfile } from "../../../Service/Apis";
import { ThunkDispatch } from "@reduxjs/toolkit";
import { RootState } from "../../../store/Store";
import { useDispatch, useSelector } from "react-redux";
import MonthButtonGroup from "../../utilization/MonthsButtonGroup";
import { APIDataItem } from "../../../types";
import { useCustomToast } from "../../Methods/SnackBar";
import {
	jiraChartBlobDownload,
	resolveJiraExportEmployeeIds,
} from "../chartExport";
import { isEmployeeAccessRole } from "../../../utils/accessRole";

const MONTH_TO_LABEL: Record<string, string> = {
	"1": "1M",
	"3": "3M",
	"6": "6M",
	"12": "1Y",
};

export type UtilizationChartProps = {
	employeeId?: string;
	controlledMonth?: string;
	hideMonthButtons?: boolean;
	/** One line per project (share of weekly logged hours) for a single employee. */
	projectLinesForEmployee?: boolean;
	hideSelectAllEmployeesSwitch?: boolean;
};

const UtilizationChart = (props: UtilizationChartProps = {}) => {
	const {
		employeeId,
		controlledMonth,
		hideMonthButtons,
		projectLinesForEmployee,
		hideSelectAllEmployeesSwitch,
	} = props;
	const dispatch2: ThunkDispatch<
		RootState,
		null,
		FetchDataSuccessAction | FetchDataFailureAction
	> = useDispatch();
	const [utilizationData, setUtilizationData] = useState<any>([]);
	const [teamName, setTeamName] = useState<string>("");
	const [month, setMonth] = useState<string>("1");
	const [selectedFormat, setSelectedFormat] = useState<string>("excel");
	const showToast = useCustomToast();
	const [selectedEmployees, setSelectedEmployees] = useState<string[]>([]);
	const [loading, setLoading] = useState(false);
	const [downloading, setDownloading] = useState(false);
	const [selectAll, setSelectAll] = useState(true);

	const abortControllerRef = useRef<AbortController | null>(null);

	const nrole = useSelector((state: any) => state.auth.selectedRole);
	const paramsData = useMemo(
		() => ({
			...jiraChartResponseProfile,
			role: nrole ?? "Employee",
			...(employeeId ? { employee_ids: [employeeId] } : {}),
		}),
		[nrole, employeeId]
	);

	const fetchUtilizationData = useCallback(
		async (selectedMonth: string) => {
			if (abortControllerRef.current) {
				abortControllerRef.current.abort();
			}
			const controller = new AbortController();
			abortControllerRef.current = controller;

			try {
				setLoading(true);
				const res = await dispatch2(
					fetchData(`${getUtilizationData}${selectedMonth}`, paramsData, {
						signal: controller.signal,
					})
				);

				if (controller.signal.aborted) return;

				setMonth(selectedMonth);
				setUtilizationData(res);
			} catch (error) {
				if (!axios.isCancel(error)) {
					console.error(error);
				}
			} finally {
				if (!controller.signal.aborted) {
					setLoading(false);
				}
			}
		},
		[dispatch2, paramsData]
	);

	useEffect(() => {
		fetchUtilizationData(controlledMonth ?? "1");

		return () => {
			if (abortControllerRef.current) {
				abortControllerRef.current.abort();
			}
		};
	}, [fetchUtilizationData, controlledMonth]);

	const handleMonthChange = (selectedMonth: string) => {
		fetchUtilizationData(selectedMonth);
	};

	const buttonData = [
		{ label: "1M", onClick: () => handleMonthChange("1") },
		{ label: "3M", onClick: () => handleMonthChange("3") },
		{ label: "6M", onClick: () => handleMonthChange("6") },
		{ label: "1Y", onClick: () => handleMonthChange("12") },
	];

	useEffect(() => {
		if (utilizationData?.teams?.length && !teamName) {
			setTeamName(utilizationData.teams[0].team_name);
		}
	}, [utilizationData, teamName]);

	useEffect(() => {
		if (!utilizationData || loading) return;
		if (projectLinesForEmployee && employeeId) {
			const selectedTeam =
				utilizationData?.teams?.find(
					(team: any) => team.team_name === teamName
				) || utilizationData?.teams?.[0];
			const utilizations =
				selectedTeam?.utilizations || utilizationData?.utilizations || [];
			const names = new Set<string>();
			for (const item of utilizations) {
				for (const p of item.project_breakdown || []) {
					const label = String(
						p.project_name || p.project_key || "Unknown"
					);
					names.add(label);
				}
			}
			setSelectedEmployees(Array.from(names));
			setSelectAll(true);
			return;
		}
		if (utilizationData.teams?.length) {
			const tn =
				teamName ||
				utilizationData.teams[0]?.team_name;
			const t = utilizationData.teams.find(
				(x: any) => x.team_name === tn
			);
			if (!t?.employees?.length) return;
			setSelectedEmployees(
				t.employees.map((e: any) => e.employee_name)
			);
			setSelectAll(true);
		} else if (utilizationData.utilizations?.length) {
			const names: string[] = Array.from(
				new Set(
					utilizationData.utilizations.map(
						(u: any) => String(u.employee_name)
					)
				)
			);
			setSelectedEmployees(names);
			setSelectAll(true);
		}
	}, [
		utilizationData,
		loading,
		teamName,
		projectLinesForEmployee,
		employeeId,
	]);

	const computedData = useMemo(() => {
		if (projectLinesForEmployee && employeeId) {
			const selectedTeam =
				utilizationData?.teams?.find(
					(team: any) => team.team_name === teamName
				) || utilizationData?.teams?.[0];
			const utilizations =
				selectedTeam?.utilizations || utilizationData?.utilizations || [];
			const rows: {
				employee_name: string;
				employee_id: string;
				week: string;
				percent: number;
				project_breakdown?: {
					project_name?: string | null;
					project_key?: string | null;
					hours_logged?: number | null;
				}[];
			}[] = [];
			for (const item of utilizations) {
				const breakdown = item.project_breakdown || [];
				const totalH = breakdown.reduce(
					(s: number, p: any) => s + (Number(p.hours_logged) || 0),
					0
				);
				for (const p of breakdown) {
					const hrs = Number(p.hours_logged) || 0;
					const pct = totalH > 0 ? (hrs / totalH) * 100 : 0;
					const label = String(
						p.project_name || p.project_key || "Unknown"
					);
					rows.push({
						employee_name: label,
						employee_id: String(p.project_key || label),
						week: item.week_end_date,
						percent: pct,
						project_breakdown: [
							{
								project_name: p.project_name,
								project_key: p.project_key,
								hours_logged: p.hours_logged,
							},
						],
					});
				}
			}
			return rows;
		}

		const selectedTeam =
			utilizationData?.teams?.find(
				(team: any) => team.team_name === teamName
			) || utilizationData?.teams?.[0];

		return selectedTeam
			? selectedTeam?.utilizations?.map((item: any) => ({
					employee_name: item.employee_name,
					employee_id: item.employee_id,
					week: item.week_end_date,
					percent: item.utilization,
					project_breakdown: item.project_breakdown ?? [],
			  })) || []
			: utilizationData?.utilizations?.map((item: APIDataItem) => ({
					employee_name: item.employee_name,
					employee_id: item.employee_id,
					week: item.week_end_date,
					percent: item.utilization,
					project_breakdown: item.project_breakdown ?? [],
			  })) || [];
	}, [utilizationData, teamName, projectLinesForEmployee, employeeId]);

	const chartEmployeeNames = useMemo((): string[] => {
		return Array.from(
			new Set(
				computedData.map((item: any) => String(item.employee_name))
			)
		);
	}, [computedData]);

	const onDownload = async () => {
		try {
			setDownloading(true);
			const isEmployeeRole =
				(nrole ?? "Employee").toLowerCase() === "employee";

			let selectedTeam: any = null;
			if (!isEmployeeRole) {
				if (!teamName) {
					throw new Error("Please select a team");
				}
				selectedTeam = utilizationData.teams?.find(
					(team: any) => team.team_name === teamName
				);
				if (!selectedTeam) {
					throw new Error("Selected team not found");
				}
			}

			const employeeIds = resolveJiraExportEmployeeIds({
				selectedEmployees,
				chartEmployeeNames,
				teamEmployees: selectedTeam?.employees,
				rowsForIdFallback: computedData as any[],
			});

			const baseParams: Record<string, unknown> = {
				...(paramsData as Record<string, unknown>),
				download: selectedFormat,
			};
			if (employeeId) {
				baseParams.employee_ids = [employeeId];
			} else if (employeeIds) {
				baseParams.employee_ids = employeeIds;
			}

			if (!isEmployeeRole && selectedTeam) {
				baseParams.team_id = selectedTeam.team_id;
			}

			const ext =
				selectedFormat === "excel" ? "xlsx" : selectedFormat;

			const filename = !isEmployeeRole
				? `utilization-${teamName.replace(/\s+/g, "-")}.${ext}`
				: `utilization.${ext}`;

			await jiraChartBlobDownload(
				`${getUtilizationData}${month}`,
				baseParams,
				filename,
				showToast
			);
		} catch (error: any) {
			showToast(error?.message ?? String(error), "error", true);
		} finally {
			setDownloading(false);
		}
	};

	const handleEmployeeSelection = (employeeName: string) => {
		const newSelectedEmployees = selectedEmployees.includes(
			employeeName
		)
			? selectedEmployees.filter((name) => name !== employeeName)
			: [...selectedEmployees, employeeName];

		setSelectedEmployees(newSelectedEmployees);

		const uniqueEmployees = Array.from(
			new Set(computedData.map((item: any) => item.employee_name))
		);

		if (newSelectedEmployees.length === uniqueEmployees.length) {
			setSelectAll(true);
		} else {
			setSelectAll(false);
		}
	};

	const getAllEmployeeNames = useCallback(() => {
		if (projectLinesForEmployee && employeeId) {
			return Array.from(
				new Set(computedData.map((item: any) => String(item.employee_name)))
			);
		}
		if (utilizationData?.teams?.length) {
			const t =
				utilizationData.teams.find(
					(team: any) => team.team_name === teamName
				) || utilizationData.teams[0];
			return (
				t?.employees?.map(
					(employee: any) => employee.employee_name
				) || []
			);
		}
		return Array.from(
			new Set(
				(utilizationData?.utilizations || []).map(
					(item: any) => item.employee_name
				)
			)
		);
	}, [utilizationData, teamName, projectLinesForEmployee, employeeId, computedData]);

	const handleSwitchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const checked = event.target.checked;
		setSelectAll(checked);

		if (checked) {
			setSelectedEmployees(getAllEmployeeNames());
		} else {
			setSelectedEmployees([]);
		}
	};

	const getAverageUtilization = (): string => {
		const team = utilizationData?.teams?.find(
			(t: any) => t.team_name === teamName
		);
		const avg = parseFloat(team?.average_utilization);
		return isNaN(avg) ? "N/A" : `${avg.toFixed(2)}%`;
	};

	return (
		<>
			<Paper sx={{ m: 2, p: 4 }}>
				<Box display="flex" justifyContent="space-between" alignItems="center">
					<Box
						display="flex"
						justifyContent="center"
						alignItems="center"
						flexDirection="column"
						gap={2}
					>
						<Typography variant="h4">Utilization Chart</Typography>
						{!hideMonthButtons && (
							<MonthButtonGroup
								buttonData={buttonData}
								value={MONTH_TO_LABEL[month] ?? "1M"}
							/>
						)}
					</Box>
					<Box
						display="flex"
						justifyContent="center"
						alignItems="center"
						flexDirection="column"
						gap={2}
					>
						{utilizationData?.teams && !employeeId && (
							<FormControl
								size="small"
								sx={{ minWidth: 220, width: "100%", maxWidth: 320 }}
							>
								<InputLabel id="utilization-team-select-label">
									Select Team
								</InputLabel>
								<Select
									labelId="utilization-team-select-label"
									id="utilization-team-select"
									value={teamName}
									label="Select Team"
									onChange={(e) =>
										setTeamName(e.target.value)
									}
								>
									{utilizationData?.teams?.map(
										(team: any) => (
											<MenuItem
												key={team.team_id}
												value={team.team_name}
											>
												{team.team_name}
											</MenuItem>
										)
									)}
								</Select>
							</FormControl>
						)}

						<Box
							display="flex"
							justifyContent="center"
							gap={2}
							alignItems="center"
							flexWrap="wrap"
						>
							<FormControl size="small" sx={{ minWidth: 120 }}>
								<InputLabel id="export-format-label">
									Format
								</InputLabel>
								<Select
									labelId="export-format-label"
									label="Format"
									value={selectedFormat}
									onChange={(e) =>
										setSelectedFormat(e.target.value)
									}
								>
									<MenuItem value="excel">
										Excel (.xlsx)
									</MenuItem>
									<MenuItem value="csv">CSV</MenuItem>
									<MenuItem value="json">JSON</MenuItem>
								</Select>
							</FormControl>
							{downloading ? (
								<Box
									display="flex"
									justifyContent="center"
									alignItems="center"
									gap={2}
								>
									<CircularProgress size="1rem" />
									<Typography>Downloading...</Typography>
								</Box>
							) : (
								<Button
									variant="outlined"
									size="small"
									onClick={onDownload}
								>
									Export
								</Button>
							)}
						</Box>
					</Box>
				</Box>
				{!hideSelectAllEmployeesSwitch && !isEmployeeAccessRole(nrole) && (
					<Box
						display="flex"
						justifyContent="flex-end"
						alignItems="center"
						gap={2}
						m={2}
						width="100%"
					>
						<Typography>Select All Employees</Typography>
						<Switch
							checked={selectAll}
							onChange={handleSwitchChange}
							color="primary"
						/>
					</Box>
				)}
				{loading ? (
					<Box
						display="flex"
						justifyContent="center"
						alignItems="center"
						width="100%"
						height={200}
					>
						<CircularProgress />
					</Box>
				) : (
					<Box
						display="flex"
						justifyContent="center"
						alignItems="center"
						flexDirection="column"
					>
						<LineChartComponent
							data={computedData || []}
							YAxisLabel={
								projectLinesForEmployee
									? "Share of logged time (%)"
									: "Percentage (%)"
							}
							selectedEmployees={selectedEmployees}
							onEmployeeSelection={handleEmployeeSelection}
						/>
						{utilizationData?.teams && teamName && !projectLinesForEmployee && (
							<Box
								display="flex"
								justifyContent="flex-end"
								alignItems="center"
								width="100%"
								mt={2}
								sx={{
									borderTop: "1px solid #ccc",
									paddingTop: 2,
								}}
							>
								<Typography
									variant="h6"
									sx={{
										fontSize: "1rem",
										fontWeight: "bold",
									}}
								>
									Average Utilization:&nbsp;
									{getAverageUtilization()}
								</Typography>
							</Box>
						)}
					</Box>
				)}
			</Paper>
		</>
	);
};

export default UtilizationChart;
